/**
 * Typing once and having it arrive in several terminals.
 *
 * The one feature an SSH console needs that an agent cockpit does not: seven
 * machines that must all be told the same thing. TUIOS calls it multifocus; the
 * shape here is a small bus rather than a shared input path, because each pane
 * already owns its own socket, its own client id and its own input sequence, and
 * exactly-once delivery depends on all three staying per-pane. So a relayed
 * keystroke goes out through each pane's *own* sender and is numbered in that
 * pane's own sequence — the same journey it would have made had somebody typed it
 * there.
 *
 * Three rules this enforces, and each of them is here because the alternative is
 * a command running somewhere nobody meant:
 *
 *   - **It is never on by default and never remembered.** A broadcast mode an
 *     operator forgot they left on is how one `rm -rf` becomes seven. It dies
 *     with the page, deliberately, and is not written to storage anywhere.
 *   - **Only panes that are listening receive**, and a pane can be muted
 *     individually. The wall is the set; muting is the exception.
 *   - **The panes that will receive are marked while it is on.** A relay an
 *     operator cannot see is one they cannot check before pressing Enter.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

export interface Broadcast {
  /** Whether relaying is on at all. */
  enabled: boolean
  /** Whether this session would receive what is typed elsewhere. */
  listening: (sessionId: string) => boolean
  /** Turn a single pane's listening off and on while the mode is enabled. */
  toggleMuted: (sessionId: string) => void
  /** How many panes would receive. Zero means the mode is on but does nothing. */
  count: number

  /** A pane offers its input sink for as long as it is mounted. */
  register: (sessionId: string, send: (data: string) => void) => () => void
  /** The pane being typed into hands the keystroke on. */
  relay: (fromSessionId: string, data: string) => void
}

const inert: Broadcast = {
  enabled: false,
  listening: () => false,
  toggleMuted: () => {},
  count: 0,
  register: () => () => {},
  relay: () => {},
}

const Ctx = createContext<Broadcast>(inert)

/** Panes read this. Outside a provider it is inert, so a screen that does not
 *  offer broadcasting costs nothing and behaves as it always did. */
export function useBroadcast(): Broadcast {
  return useContext(Ctx)
}

export function BroadcastProvider({
  enabled,
  members,
  children,
}: {
  enabled: boolean
  /** The sessions on the wall. Membership is the wall's business; this only says
   *  which of them are muted. */
  members: readonly string[]
  children: ReactNode
}) {
  const [muted, setMuted] = useState<ReadonlySet<string>>(() => new Set())
  // A ref, not state: registering a sink must not re-render every pane, and the
  // relay reads it at the moment a key is pressed rather than at render time.
  const sinks = useRef(new Map<string, (data: string) => void>())

  const register = useCallback((sessionId: string, send: (data: string) => void) => {
    sinks.current.set(sessionId, send)
    return () => {
      // Only if it is still ours. A pane that remounted has already replaced the
      // entry, and a late cleanup would delete the new one.
      if (sinks.current.get(sessionId) === send) sinks.current.delete(sessionId)
    }
  }, [])

  const listening = useCallback(
    (sessionId: string) => enabled && members.includes(sessionId) && !muted.has(sessionId),
    [enabled, members, muted],
  )

  const relay = useCallback(
    (fromSessionId: string, data: string) => {
      if (!enabled) return
      for (const id of members) {
        // Not back to the sender: it has already sent this itself, and echoing
        // would double every keystroke in the pane being typed into.
        if (id === fromSessionId || muted.has(id)) continue
        sinks.current.get(id)?.(data)
      }
    },
    [enabled, members, muted],
  )

  const toggleMuted = useCallback((sessionId: string) => {
    setMuted((prev) => {
      const next = new Set(prev)
      if (next.has(sessionId)) next.delete(sessionId)
      else next.add(sessionId)
      return next
    })
  }, [])

  const count = useMemo(
    () => (enabled ? members.filter((id) => !muted.has(id)).length : 0),
    [enabled, members, muted],
  )

  const value = useMemo<Broadcast>(
    () => ({ enabled, listening, toggleMuted, count, register, relay }),
    [enabled, listening, toggleMuted, count, register, relay],
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
